import type { Edge } from "../nav/graph.js";

export type PlayResult = "ended" | "skipped" | "failed";

export interface PlayerOptions {
  reducedMotion: boolean;
  /** Turns a media path (`transitions/a__b`) into a URL (without extension). */
  mediaUrl: (relative: string) => string;
  supportsWebm: boolean;
  /** Cross-fade length when the video layer is released, ms. */
  fadeMs?: number;
  /** Give up waiting for a clip to become playable after this long, ms. */
  readyTimeoutMs?: number;
  /** Max pooled (preloaded) clips. */
  poolSize?: number;
  createVideo?: () => HTMLVideoElement;
}

/**
 * Plays walk-through clips on a video layer that sits above the still.
 *
 * - Neighbour clips are primed ahead of time in a small pool of `<video preload=auto>` elements.
 * - `play()` mounts a pooled element, reveals it on the first real frame (no black flash), and resolves on end/skip/failure.
 * - The caller then swaps the still underneath and calls `release()`, which cross-fades the video out.
 *   Because the clip's last frame matches the destination still, the hand-over is seamless.
 * - With reduced motion the player never plays video: `play()` resolves "skipped" immediately.
 */
export class TransitionPlayer {
  private readonly pool = new Map<string, HTMLVideoElement>();
  private active: HTMLVideoElement | undefined;
  private finish: ((r: PlayResult) => void) | undefined;
  private releaseTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly layer: HTMLElement,
    private readonly opts: PlayerOptions
  ) {}

  get playing(): boolean {
    return this.finish !== undefined;
  }

  urlFor(edge: Edge): string {
    return `${this.opts.mediaUrl(edge.video)}.${this.opts.supportsWebm ? "webm" : "mp4"}`;
  }

  /** Prime clips for likely next moves. Cheap to call repeatedly. */
  preload(edges: readonly Edge[]): void {
    if (this.opts.reducedMotion) return;
    for (const e of edges) this.video(this.urlFor(e));
  }

  async play(edge: Edge, posterUrl?: string): Promise<PlayResult> {
    if (this.opts.reducedMotion || this.finish) return "skipped";
    clearTimeout(this.releaseTimer);
    const video = this.video(this.urlFor(edge));
    if (posterUrl) video.poster = posterUrl;
    this.active = video;
    this.layer.querySelectorAll("video").forEach((v) => v !== video && v.remove());
    video.style.opacity = "0";
    try {
      video.currentTime = 0;
    } catch {
      /* not seekable yet; starts at 0 anyway */
    }
    this.layer.append(video);

    const ready = await this.waitReady(video);
    if (!ready) {
      this.detach(video);
      return "failed";
    }
    return new Promise<PlayResult>((resolve) => {
      let settled = false;
      const done = (r: PlayResult) => {
        if (settled) return;
        settled = true;
        video.removeEventListener("ended", onEnded);
        video.removeEventListener("error", onError);
        this.finish = undefined;
        resolve(r);
      };
      const onEnded = () => done("ended");
      const onError = () => {
        this.detach(video);
        done("failed");
      };
      video.addEventListener("ended", onEnded);
      video.addEventListener("error", onError);
      this.finish = done;
      video.play().then(
        () => {
          video.style.opacity = "1";
        },
        () => {
          this.detach(video);
          done("failed");
        }
      );
    });
  }

  /** Jump to the end of the current clip (click / Escape). The frame on screen stays for the cross-fade. */
  skip(): void {
    if (!this.finish) return;
    this.active?.pause();
    this.finish("skipped");
  }

  /** Fade the video layer out (call after swapping the still underneath). */
  release(): void {
    const video = this.active;
    if (!video) return;
    this.active = undefined;
    video.style.opacity = "0";
    clearTimeout(this.releaseTimer);
    this.releaseTimer = setTimeout(() => this.detach(video), this.opts.fadeMs ?? 350);
  }

  private detach(video: HTMLVideoElement): void {
    video.pause();
    video.remove();
    if (this.active === video) this.active = undefined;
  }

  private video(url: string): HTMLVideoElement {
    const cached = this.pool.get(url);
    if (cached) {
      this.pool.delete(url);
      this.pool.set(url, cached); // most recently used last
      return cached;
    }
    const v = this.opts.createVideo ? this.opts.createVideo() : document.createElement("video");
    v.className = "transition-video";
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.setAttribute("aria-hidden", "true");
    v.src = url;
    this.pool.set(url, v);
    const max = this.opts.poolSize ?? 6;
    for (const [key, old] of this.pool) {
      if (this.pool.size <= max) break;
      if (old !== this.active) {
        old.removeAttribute("src");
        old.load?.();
        this.pool.delete(key);
      }
    }
    return v;
  }

  private waitReady(video: HTMLVideoElement): Promise<boolean> {
    if (video.readyState >= 3) return Promise.resolve(true);
    return new Promise((resolve) => {
      const clean = () => {
        clearTimeout(timer);
        video.removeEventListener("canplay", ok);
        video.removeEventListener("error", bad);
      };
      const ok = () => (clean(), resolve(true));
      const bad = () => (clean(), resolve(false));
      const timer = setTimeout(bad, this.opts.readyTimeoutMs ?? 5000);
      video.addEventListener("canplay", ok);
      video.addEventListener("error", bad);
    });
  }
}
